export interface SignalFilters {}

export type SignalId = string;

export const signalTypeMap = {
    adNetwork: "AdNetwork",
    technology: "Technology",
    traffic: "Traffic",
    ecommerce: "Ecommerce",
    news: "News",
    intent: "Intent",
    advertiserActivity: "AdvertiserActivity",
    apps: "Apps",
    roleChanges: "RoleChanges",
} as const;
export type SignalType = (typeof signalTypeMap)[keyof typeof signalTypeMap];

/** @deprecated use `signalTypeMap`  */
export enum SignalTypeEnum {
    AdNetwork = "AdNetwork",
    Technology = "Technology",
    Traffic = "Traffic",
    Ecommerce = "Ecommerce",
    News = "News",
    Intent = "Intent",
    AdvertiserActivity = "AdvertiserActivity",
    Apps = "Apps",
    RoleChanges = "RoleChanges",
}

export const signalSourceMap = {
    api: "api",
    platform: "platform",
    account: "account",
    restApi: "restApi",
} as const;
export type SignalSource = (typeof signalSourceMap)[keyof typeof signalSourceMap];

/** @deprecated use `SignalSource`  */
export enum SignalSourceEnum {
    Api = "api",
    Platform = "platform",
    Account = "account",
    RestApi = "restApi",
}

/**
 * Represents the lifecycle status of a signal
 *
 * @remarks
 * Signals can be in one of two lifecycle states:
 * - `draft`: Temporary signals with expiration dates
 * - `permanent`: Signals without expiration dates, that are considered as user assets
 */
export type SignalLifecycleStatus = "draft" | "permanent";

/**
 * Shared properties for all signal variants
 *
 * @template Filters - The type of filters applied to this signal
 *
 * @remarks
 * This base interface contains properties common to all signal types
 * Use the discriminated union type `Signal` for working with signals,
 * which ensures type safety across different lifecycle variants
 */
interface SignalBase<Filters extends SignalFilters = SignalFilters> {
    readonly id: SignalId;
    readonly signalType: SignalType;
    readonly signalSubType?: string;
    readonly signalSource: SignalSource;
    readonly isDefault: boolean;
    readonly filters: Readonly<Partial<Filters>>;
    readonly createdAt: string;
    readonly updatedAt?: string;
    readonly templateHint?: string;
    readonly accountSignalId?: string;
}

/**
 * Draft signal with expiration date
 *
 * @template Filters - The type of filters applied to this signal
 *
 * @remarks
 * Draft signals are temporary and have an expiration date.
 * After expiration, they should be filtered out from most UI contexts
 * Use `isSignalExpired()` to check expiration status
 */
export interface IDraftSignal<Filters extends SignalFilters = SignalFilters> extends SignalBase<Filters> {
    readonly lifecycleStatus: "draft";
    readonly expiresAt: string;
}

/**
 * Permanent signal without expiration
 *
 * @template Filters - The type of filters applied to this signal
 *
 * @remarks
 * Permanent signals are user assets that do not expire
 * They should be visible in all UI contexts
 */
export interface IPermanentSignal<Filters extends SignalFilters = SignalFilters> extends SignalBase<Filters> {
    readonly lifecycleStatus: "permanent";
}

/**
 * Discriminated union of all signal variants
 *
 * @template Filters - The type of filters applied to this signal
 */
export type Signal<Filters extends SignalFilters = SignalFilters> = IDraftSignal<Filters> | IPermanentSignal<Filters>;
